import React, { useState, useCallback, useEffect, useContext } from 'react';
import { Card, Form, Input, Select, Row, Col, Button, Divider, Checkbox } from 'antd';
import { UsersApi, OrganizationsApi, ProjectsApi } from 'api/api';
import {
  UserDto,
  UserGroupDetailsDto,
  LanguageForUiDto,
  CreateUserCommand,
  OrganizationDtoPaginatedList,
  OrganizationDto,
  ProjectDto,
} from 'api/models';
import FeatherIcon from 'feather-icons-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { BasicFormWrapper } from 'container/styled';
import Heading from 'components/heading/heading';
import { useDebouncedValue } from 'hooks/useDebouncedValue';
import openNotificationWithIcon from 'utility/notification';
import { CommonDataContext } from 'contexts/CommonDataContext/CommonDataContext';
import isSuperAdminUser from 'utility/isSuperAdminUser';

const { Option } = Select;
const { TextArea } = Input;

interface UserWizardProps {
  user: UserDto | null;
}

export const UserWizzard = ({ user }: UserWizardProps) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const isSuperAdministrator = isSuperAdminUser();
  const commonData = useContext(CommonDataContext);
  const [form] = Form.useForm<CreateUserCommand>();
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [loading, setIsLoading] = useState<boolean>(false);
  const [userDefaultLanguage, setUserDefaultLanguage] = useState<Number>(-1);
  const [userAlreadyExists, setUserAlreadyExists] = useState<boolean>(false);
  const [organizations, setOrganizations] = useState<OrganizationDto[]>();
  const [projects, setProjects] = useState<ProjectDto[]>();
  const [projectsLoading, setProjectsLoading] = useState<boolean>(false);
  const { userGroups, languages } = commonData as any;
  const usersApi = new UsersApi();
  const organizationsApi = new OrganizationsApi();
  const projectsApi = new ProjectsApi();
  const [hasLimitedAccess, setHasLimitedAccess] = useState<boolean>(false);

  const emailDebouncedValue = useDebouncedValue({
    delay: 300,
    value: Form.useWatch('email', form as any),
  });

  const validateUser = useCallback(async () => {
    const email = form.getFieldValue('email')?.trim();
    if (!email) return;

    try {
      const response = await usersApi.apiUsersAvailableEmailGet({ email });
      const exists = !response.data;
      setUserAlreadyExists(exists);

      if (exists) {
        form.setFields([
          {
            name: 'email',
            errors: [t('validations.user-email-already-exists', 'User email already exists!')],
          },
        ]);
      }
    } catch (err) {
      console.error(err);
    }
  }, [form, t]);

  useEffect(() => {
    if (!user) validateUser();
  }, [user, emailDebouncedValue, validateUser]);

  useEffect(() => {
    if (languages && languages.length > 0) {
      const langId = (languages as LanguageForUiDto[]).find(
        (x: LanguageForUiDto) => x.code == localStorage.getItem('i18nextLng'),
      );
      setUserDefaultLanguage(langId?.id as Number);
    }
  }, [languages]);

  const fetchOrganizations = async () => {
    try {
      setIsLoading(true);
      const response = await organizationsApi.apiOrganizationsGet({ pageSize: -1 });

      setOrganizations(response.data.items as OrganizationDto[]);
    } catch (err) {
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isSuperAdministrator) {
      fetchOrganizations();
    }
  }, []);

  const fetchProjects = async () => {
    try {
      setProjectsLoading(true);
      const response = await projectsApi.apiProjectsGet({ pageSize: -1 });
      setProjects(response.data.items as ProjectDto[]);
    } catch (err) {
    } finally {
      setProjectsLoading(false);
    }
  };

  useEffect(() => {
    if (user) {
      setHasLimitedAccess(user.hasLimitedProjectAccess as boolean);

      form.setFieldsValue({
        firstName: user.firstName,
        lastName: user.lastName,
        phoneNumber: user.phoneNumber,
        email: user.email,
        languageId: user.languageId || undefined,
        userGroups: user.userGroups as string[],
        projectIds: user.projectAccessIds as number[],
      });

      fetchProjects();
    }
  }, [user, form]);

  const handleSubmit = async (values: CreateUserCommand) => {
    const command: CreateUserCommand = {
      firstName: values.firstName ?? '',
      lastName: values.lastName ?? '',
      email: values.email ?? '',
      phoneNumber: values.phoneNumber,
      languageId: values.languageId,
      userGroups: values.userGroups,
      projectIds: hasLimitedAccess ? values.projectIds : undefined,
    };

    console.log(command);
    if (userAlreadyExists) return;
    setIsSaving(true);

    try {
      if (user) {
        await usersApi.apiUsersIdPut({ id: user.id as string, updateUserCommand: values });
      } else {
        await usersApi.apiUsersPost({ createUserCommand: command });
      }

      navigate('/users');
      openNotificationWithIcon(
        'success',
        user ? t('users:user.updated', 'User Updated') : t('users:user.created', 'User Created'),
        user
          ? t('users:user.updated.description', 'User has been successfully updated')
          : t('users:user.created.description', 'User has been successfully created'),
      );
    } catch (err) {
      openNotificationWithIcon(
        'error',
        user
          ? t('users:failed.update.user', 'Failed to Update User')
          : t('users:failed.create.user', 'Failed to Create User'),
        user
          ? t('users:failed.update.user.description', 'There was an error updating the user')
          : t('users:failed.create.user.description', 'There was an error creating the user'),
      );
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Card loading={loading}>
      <BasicFormWrapper>
        <Row justify="center">
          <Col xs={24} md={20} lg={18}>
            <Form form={form} layout="vertical" onFinish={handleSubmit}>
              {/* User Information Section */}
              <Heading as="h4">{t('user-information', 'User Information')}</Heading>
              <Row gutter={[16, 24]} className="mt-4">
                {isSuperAdministrator && (
                  <Col xs={24} md={12}>
                    <Form.Item
                      name="organizationId"
                      label={t('global:select.organization', 'Select Organization')}
                      rules={[
                        {
                          required: true,
                          message: t(
                            'validations.organization.required',
                            'Please select organization user will belong to',
                          ),
                        },
                      ]}
                    >
                      <Select>
                        {organizations?.map((item: OrganizationDto) => (
                          <Option key={item.id} value={item.name}>
                            {item.name}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                )}
                <Col xs={24} md={12}>
                  <Form.Item name="firstName" label={t('global:first.name', 'First Name')} rules={[{ required: true }]}>
                    <Input prefix={<FeatherIcon icon="user" size={16} />} disabled={userAlreadyExists} />
                  </Form.Item>
                </Col>

                <Col xs={24} md={12}>
                  <Form.Item name="lastName" label={t('global:last.name', 'Last Name')} rules={[{ required: true }]}>
                    <Input prefix={<FeatherIcon icon="users" size={16} />} disabled={userAlreadyExists} />
                  </Form.Item>
                </Col>

                <Col xs={24} md={12}>
                  <Form.Item
                    name="email"
                    label={t('global:email', 'Email')}
                    tooltip={t('users:email.usage.purpose.tooltip', 'This email will be used for logging into the app')}
                    rules={[
                      {
                        required: true,
                        type: 'email',
                        message: t('users.create:email-validation', 'Email is required'),
                      },
                    ]}
                  >
                    <Input prefix={<FeatherIcon icon="mail" size={16} />} disabled={!!user} />
                  </Form.Item>
                </Col>

                <Col xs={24} md={12}>
                  <Form.Item name="phoneNumber" label={t('global.phone-number', 'Phone Number')}>
                    <Input prefix={<FeatherIcon icon="phone" size={16} />} disabled={userAlreadyExists} />
                  </Form.Item>
                </Col>
              </Row>

              <Divider />

              {/* User Settings Section */}
              <Heading as="h4">{t('user-settings', 'User Settings')}</Heading>
              <Row gutter={[16, 24]} className="mt-4">
                <Col xs={24} md={12}>
                  <Form.Item
                    name="userGroups"
                    label={t('global:select.user.groups', 'Select User Groups')}
                    rules={[
                      {
                        required: true,
                        message: t('validations.user.group.required', 'Please select at least one group'),
                      },
                    ]}
                  >
                    <Select mode="multiple">
                      {userGroups?.map((item: UserGroupDetailsDto) => (
                        <Option key={item.name} value={item.name}>
                          {item.name}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>

                <Col xs={24} md={12}>
                  <Form.Item
                    name="languageId"
                    label={t('system-languages.default-language', 'Default Language')}
                    rules={[{ required: true, message: t('validations:language.required', 'Please select language') }]}
                  >
                    <Select disabled={userAlreadyExists} value={userDefaultLanguage}>
                      {languages?.map((item: LanguageForUiDto) => (
                        <Option key={item.id} value={item.id}>
                          {item.name}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
              </Row>

              <Divider />

              {/* Project Access Section */}
              <Heading as="h4">{t('project-access', 'Project Access')}</Heading>
              <Row gutter={[16, 24]} className="mt-4">
                <Col xs={24}>
                  <div className="mb-3" style={{ color: '#666' }}>
                    <FeatherIcon icon="info" size={16} style={{ marginRight: '5px' }} />
                    {t('project-access.info-default', 'By default, this user will have access to all projects')}
                  </div>

                  <Checkbox
                    checked={hasLimitedAccess}
                    onChange={e => {
                      setHasLimitedAccess(e.target.checked);
                      if (!e.target.checked) {
                        form.setFieldValue('projectIds', undefined);
                      } else if (!user) {
                        fetchProjects();
                      }
                    }}
                  >
                    {hasLimitedAccess
                      ? t('grant-full-access', 'Grant Full Access')
                      : t('give-limited-access', 'Give Limited Access')}
                  </Checkbox>
                </Col>

                {hasLimitedAccess && (
                  <Col xs={24}>
                    <Form.Item
                      name="projectIds"
                      label={t('select-projects', 'Select Projects')}
                      rules={[
                        {
                          required: true,
                          message: t('validations.projects.required', 'Please select at least one project'),
                        },
                      ]}
                    >
                      <Select
                        mode="multiple"
                        loading={projectsLoading}
                        placeholder={t('select-projects-placeholder', 'Select projects to grant access')}
                      >
                        {projects?.map(project => (
                          <Option key={project.id} value={project.id}>
                            {project.name}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                )}
              </Row>

              <div style={{ textAlign: 'right', marginTop: '24px' }}>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={isSaving}
                  disabled={userAlreadyExists}
                  icon={<FeatherIcon icon={user ? 'save' : 'user-check'} size={16} />}
                >
                  {user ? t('global:update', 'Update User') : t('users:complete.registration', 'Complete Registration')}
                </Button>
              </div>
            </Form>
          </Col>
        </Row>
      </BasicFormWrapper>
    </Card>
  );
};
